import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  IconButton,
  Paper,
  Grid,
  useTheme
} from "@mui/material";
import {
  Close,
  CloudUpload,
} from "@mui/icons-material";

import { RootState } from "../../store/Index";
import apiService from "../../service/Api";
import CustomTextField from "../../components/ui/Textfield";
import SubmissionModal from "../../components/ui/SuccessModal";
import CardSelector from "../../components/ui/CardSelector";

import { EditCrewDialogProps, EditPilotFormProps, PilotAPIResponse } from "./types/view-pilot";
import { RoleSkillTool } from "./types/add-crew-member";
import useDebounce from "../../hooks/useDebounce";
import { DocumentIcon } from "../../components/icons";
import { tokens } from "../../theme";
import ResizableTextarea from "../../components/ui/ResizableTextArea";
import { AxiosError } from "axios";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export default function EditCrewDialog({
  open,
  onClose,
  pilot,
}: EditCrewDialogProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<EditPilotFormProps>({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    phone: "",
    role: "",
    organization: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    marital_status: "",
    national_id: "",
    residence_country: "",
    emergency_contact: "",
    emergency_contact_phone: "",
    date_of_birth: "",
    occupation: "",
    roles: [],
    skills: [],
    tools: [],
    preferences: "",
    bio: "",
    experience_years: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  // const [newSkill, setNewSkill] = useState("");
  // const [newTool, setNewTool] = useState("");
  // const [documents, setDocuments] = useState([
  //   "National_ID.pdf",
  //   "Passport.pdf",
  //   "Employee Details.xlsx",
  // ]);

  const [pilotDocuments, setPilotDocuments] = useState<string[]>([]);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const [selectedRoles, setSelectedRoles] = useState<RoleSkillTool[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleSkillTool[]>([]);
  const [roleValue, setRoleValue] = useState("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const [selectedSkills, setSelectedSkills] = useState<RoleSkillTool[]>([]);
  const [skillOptions, setSkillOptions] = useState<RoleSkillTool[]>([]);
  const [skillValue, setSkillValue] = useState("");
  const [skillsLoading, setSkillsLoading] = useState(false);

  const [selectedTools, setSelectedTools] = useState<RoleSkillTool[]>([]);
  const [toolOptions, setToolOptions] = useState<RoleSkillTool[]>([]);  
  const [toolValue, setToolValue] = useState("");
  const [toolsLoading, setToolsLoading] = useState(false);

  const debouncedRoleSearch = useDebounce(roleValue, 400);
  const debouncedSkillSearch = useDebounce(skillValue, 400);
  const debouncedToolSearch = useDebounce(toolValue, 400);
  
  useEffect(() => {
    if (pilot) {
      setSelectedRoles(pilot.rolesDetail);
      setSelectedSkills(pilot.skillsDetail);
      setSelectedTools(pilot.toolsDetail);
      setFormData((prev: EditPilotFormProps) => ({
        ...prev,
        first_name: pilot.name,
        last_name: pilot.surname,
        email: pilot.email,
        username: pilot.username,
        phone: pilot.phone,
        role: pilot.role,
        organization: pilot.organization,
        street_address: pilot.street_address,
        city: pilot.city,
        state: pilot.state,
        postal_code: pilot.postal_code,
        residence_country: pilot.country,
        emergency_contact: pilot.emergencyContactName,
        emergency_contact_phone: pilot.emergencyContact,
        date_of_birth: pilot.date_of_birth,
        national_id: pilot.nationalId,
        occupation: pilot.occupation,
        marital_status: pilot.maritalStatus,
        preferences: pilot.preferences,
        roles: pilot.roles,
        skills: pilot.skills,
        tools: pilot.tools,
        bio: pilot.bio,
        experience_years: pilot.experience_years
      }));
    }
  }, [pilot]);

  const fetchRoles = useCallback(async (roleSearching: string) => {
      setRolesLoading(true);
      try {
        const params: Record<string, string | number> = { page_size: 20 };
        if (roleSearching) {
          params.search = roleSearching;
        }
        const roles = await apiService.get<{ results: RoleSkillTool[] }>(
          "/api/v1/crews/roles/",
          params
        );
        setRoleOptions(roles.data.results || []);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
        setRoleOptions([]);
      } finally {
        setRolesLoading(false);
      }
  }, []);

  const fetchSkills = useCallback(async (skillSearching: string) => {
      setSkillsLoading(true);
      try {
        const params: Record<string, string | number> = { page_size: 20 };
        if (skillSearching) {
          params.search = skillSearching;
        }
        const skills = await apiService.get<{ results: RoleSkillTool[] }>(
          "/api/v1/crews/skills/",
          params
        );
        setSkillOptions(skills.data.results || []);
    } catch (err) {
      console.error("Failed to fetch skills:", err);
      setSkillOptions([]);
    } finally {
      setSkillsLoading(false);
    }
  }, []);

  const fetchTools = useCallback(async (toolSearching: string) => {
    setToolsLoading(true);
    try {
      const params: Record<string, string | number> = { page_size: 20 };
      if (toolSearching) {
        params.search = toolSearching;
      }
      const tools = await apiService.get<{ results: RoleSkillTool[] }>(
        "/api/v1/crews/tools/",
        params
      );
      setToolOptions(tools.data.results || []);
    } catch (err) {
      console.error("Failed to fetch tools:", err);
      setToolOptions([]);
    } finally {
      setToolsLoading(false);
    }
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRolesChange = (roles: RoleSkillTool[]) => {
    if (selectedRoles.find(role => role.id === roles[roles.length - 1].id)) {
      toast.error(`The role ${roles[roles.length - 1].name} has already been selected`);
      return;
    }
    setSelectedRoles([ ...selectedRoles, roles[roles.length - 1]]);

    // eslint-disable-next-line prefer-const
    let roleIds: number[] = [];
    roles.forEach((role: RoleSkillTool) => {
      roleIds.push(role.id)
    });

    setFormData((prev: EditPilotFormProps) => ({
      ...prev,
      role: roles[0].name,
      roles: roleIds
    }));
  };

  const handleSkillsChange = (skills: RoleSkillTool[]) => {
    if (selectedSkills.find(skill => skill.id === skills[skills.length - 1].id)) {
      toast.error(`The skill ${skills[skills.length - 1].name} has already been selected`);
      return;
    }
    setSelectedSkills([ ...selectedSkills, skills[skills.length - 1]]);

    // eslint-disable-next-line prefer-const
    let skillIds: number[] = [];
    skills.forEach((skill: RoleSkillTool) => {
      skillIds.push(skill.id)
    });

    setFormData((prev: EditPilotFormProps) => (
      { ...prev, skills: skillIds }
    ));
  };

  const handleToolsChange = (tools: RoleSkillTool[]) => {
    if (selectedTools.find(tool => tool.id === tools[tools.length - 1].id)) {
      toast.error(`The tool ${tools[tools.length - 1].name} has already been selected`);
      return;
    }
    setSelectedTools([ ...selectedTools, tools[tools.length - 1]]);

    // eslint-disable-next-line prefer-const
    let toolIds: number[] = [];
    tools.forEach((tool: RoleSkillTool) => {
      toolIds.push(tool.id)
    });
    
    setFormData((prev: EditPilotFormProps) => (
      { ...prev, tools: toolIds }
    ));
  };

  // const addSkill = () => {
  //   if (newSkill.trim() && !skills.includes(newSkill.trim())) {
  //     setSkills([...skills, newSkill.trim()]);
  //     setNewSkill("");
  //   }
  // };

  // const addTool = () => {
  //   if (newTool.trim() && !tools.includes(newTool.trim())) {
  //     setTools([...tools, newTool.trim()]);
  //     setNewTool("");
  //   }
  // };

  const removeDocument = (docToRemove: string) => {
    setPilotDocuments(pilotDocuments.filter((doc) => doc !== docToRemove));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newDocs = Array.from(files).map((file) => file.name);
      setPilotDocuments([ ...pilotDocuments, ...newDocs ]);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleBioChange = (value: string) => {
    setFormData({
      ...formData,
      bio: value,
    });
  };

  const resetForm = () => {
    setFormData({
      bio: "",
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      phone: "",
      role: "",
      organization: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      marital_status: "",
      national_id: "",
      residence_country: "",
      emergency_contact: "",
      emergency_contact_phone: "",
      date_of_birth: "",
      occupation: "",
      roles: [],
      skills: [],
      tools: [],
      preferences: "",
      experience_years: 0
    });
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleUpdate = async () => {
    setIsModalOpen(true);
    setIsSubmitting(true);
    setIsSuccess(false);
    setErrorMessage(null);

    const { roles } = formData;

    if (!roles.length) {
      toast.error("Please select at least 1 role for this pilot.");
      setIsModalOpen(false);
      setIsSubmitting(false);
      return;
    };

    try {
      const fetchPilot = await apiService.get<PilotAPIResponse>(`/api/v1/crews/crew-members/${pilot?.id}/`);
      const pilotToUpdate = fetchPilot.data;

      if (!pilotToUpdate) return null;
      
      const payload = {
        user_id: pilotToUpdate?.user.id,
        roles: formData.roles,
        skills: formData.skills,
        tools: formData.tools,
        bio: formData.bio,
        experience_years: formData.experience_years
      };

      await apiService.patch(`/api/crews/crew-members/${pilotToUpdate.id}/full-update/`, payload);

      setIsSuccess(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorMessage(
          error?.response?.data?.message || "Failed to update pilot."
        );
      } else {
        console.error("Update failed", error);
      }
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchRoles(debouncedRoleSearch);
  }, [debouncedRoleSearch, fetchRoles]);

  useEffect(() => {
    fetchSkills(debouncedSkillSearch);
  }, [debouncedSkillSearch, fetchSkills]);

  useEffect(() => {
    fetchTools(debouncedToolSearch);
  }, [debouncedToolSearch, fetchTools]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (isSuccess) {
      window.location.reload();
      onClose();
    } else {
      onClose();
    }
    setIsSuccess(false);
    setErrorMessage(null);
  };

  if (!pilot) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          borderRadius: 2,
        },
      }}
    >
      <DialogContent>
        <Box sx={{ width: "100%" }}>
          <Box>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="edit pilot tabs"
              sx={{
                mb: 1,
                ".MuiTab-root": {
                  color: colors.primary[300],
                },
                ".Mui-selected": {
                  color: colors.primary[400],
                },
              }}
            >
              <Tab label="Basic Details" />
              <Tab label="Skills & Tools" />
              <Tab label="Documents" />
            </Tabs>
          </Box>

          {/* Basic Details Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <CustomTextField
                  name="first_name"
                  label="First Name"
                  disabled
                  fullWidth
                  value={formData.first_name}                  
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="last_name"
                  label="Last Name"
                  disabled
                  fullWidth
                  value={formData.last_name}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="email"
                  label="Email Address"
                  disabled
                  fullWidth
                  value={formData.email}
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  name="username"
                  label="Username"
                  disabled
                  fullWidth
                  value={formData.username}
                />
              </Grid>
              <Grid size={12} sx={{ borderBottom: '1px solid', borderTop: '1px solid', borderColor: 'divider', py: 1, mt: 1 }}>
                <Typography variant={'subtitle1'} fontWeight={600} mb={1}>Pilot Bio</Typography>
                <ResizableTextarea
                  label="Bio"
                  value={formData.bio || ""}
                  onChange={handleBioChange}
                  colors={colors}
                  reduxTheme={reduxTheme}
                  placeholder="Pilot Bio..."
                />
              </Grid>
              <Grid size={12}>
                <CustomTextField
                  name="experience_years"
                  label="Years Experience"
                  placeholder="Enter years of experience"
                  fullWidth
                  value={formData.experience_years}
                  onChange={(e) => handleInputChange("experience_years", e.target.value)}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Skills & Equipment Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={0}>
              <Grid size={12} sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}>
                <CardSelector
                  items={roleOptions.map((role) => ({
                    id: role.id,
                    name: role.name,
                    description: role.description || undefined,
                  }))}
                  selectedItems={selectedRoles.map((role) => ({
                    id: role.id,
                    name: role.name,
                    description: role.description || undefined,
                  }))}
                  onChange={(items) =>
                    handleRolesChange(
                      items.map(
                        (item) =>
                          roleOptions.find((r) => r.id === item.id) || roleOptions[0]
                      )
                    )
                  }
                  label="Pilot Roles"
                  searchPlaceholder="Search roles..."
                  loading={rolesLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setRoleValue(value)}
                />
              </Grid>
              <Grid size={12} sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1 }}>
                <CardSelector
                  items={skillOptions.map((skill) => ({
                    id: skill.id,
                    name: skill.name,
                    description: skill.description || undefined,
                  }))}
                  selectedItems={selectedSkills.map((skill) => ({
                    id: skill.id,
                    name: skill.name,
                    description: skill.description || undefined,
                  }))}
                  onChange={(items) =>
                    handleSkillsChange(
                      items.map(
                        (item) =>
                          skillOptions.find((s) => s.id === item.id) || skillOptions[0]
                      )
                    )
                  }
                  label="Pilot Skills"
                  searchPlaceholder="Search skills..."
                  loading={skillsLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setSkillValue(value)}
                />
              </Grid>
              <Grid size={12} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <CardSelector
                  items={toolOptions.map((tool) => ({
                    id: tool.id,
                    name: tool.name,
                    description: tool.description || undefined,
                  }))}
                  selectedItems={selectedTools.map((tool) => ({
                    id: tool.id,
                    name: tool.name,
                    description: tool.description || undefined,
                  }))}
                  onChange={(items) =>
                    handleToolsChange(
                      items.map(
                        (item) =>
                          toolOptions.find((t) => t.id === item.id) || toolOptions[0]
                      )
                    )
                  }
                  label="Pilot Work Equipment"
                  searchPlaceholder="Search tools..."
                  loading={toolsLoading}
                  multiSelect={true}
                  onSearchChange={(value) => setToolValue(value)}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Documents */}
          <TabPanel value={tabValue} index={2}>
            <Grid container>
              <Grid size={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {`[NB: Non Functional At The Moment]`}
                </Typography>
                <Paper
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    mb: 3,
                    backgroundColor: colors.primary[800],
                  }}
                >
                  <IconButton component="label">
                    <CloudUpload sx={{ fontSize: 48, color: "text.secondary" }} />
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={handleFileUpload}
                    />
                  </IconButton>
                  <Typography variant="body1">
                    Drag and drop files here, or click to select files
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Uploaded Documents
                </Typography>
                {pilotDocuments?.map((doc, index) => {
                  return (
                    <Box
                      display={'flex'}
                      key={index}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      sx={{
                        padding: 1,
                        mb: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: backgroundColor
                      }}>
                        <Box display={'flex'} alignItems={'center'} gap={1}>
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              padding: 1,
                              bgcolor: backgroundColor
                            }}>
                              <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography variant={'body2'}>{doc}</Typography>
                        </Box>
                        <Box
                          display={'flex'}
                          alignItems={'center'}
                          gap={1}>
                            <IconButton onClick={() => removeDocument(doc)}>
                              <Close sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                    </Box>
                  );
                })}
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: colors.primary[400],
            backgroundColor: colors.primary[800],
          }}
        >
          Cancel
        </Button>
        {tabValue !== 2 ? (
          <Button
            variant="contained"
            onClick={() => setTabValue(tabValue + 1)}
            sx={{
              backgroundColor: colors.primary[400],
            }}
          >
            Next Form
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{
              backgroundColor: colors.primary[400],
            }}
          >
            Update
          </Button>
        )}
      </DialogActions>
      <SubmissionModal
        open={isModalOpen}
        submitting={isSubmitting}
        success={isSuccess}
        error={errorMessage}
        successTitle="Edit Successful!"
        successMessage="Your changes have been saved."
        onClose={handleModalClose}
        onSuccessClose={handleModalClose}
      />
    </Dialog>
  );
}
